// Formatting helpers for the "Preview Ads" modal.

import { snakeCaseToPascalCase } from 'utils/helpers'

export function humanizeCallToAction(rawCallToAction, t) {
  if (!rawCallToAction) return rawCallToAction
  const fallback = snakeCaseToPascalCase(rawCallToAction)
  if (!t) return fallback
  return t([`Recommendations.campaign.setup.call_to_action.${rawCallToAction}`, fallback])
}

const KNOWN_RATIOS = [
  { label: '1:1', value: 1 },
  { label: '4:5', value: 4 / 5 },
  { label: '9:16', value: 9 / 16 },
  { label: '16:9', value: 16 / 9 },
  { label: '1.91:1', value: 1.91 },
]

const KNOWN_RATIO_TOLERANCE = 0.03

// Matches a media item's pixel dimensions to a recognizable ad-ratio label
// (e.g. "1:1", "4:5") when close enough, falling back to a computed decimal
// ratio otherwise — mirrors the "1:1 • 1080 × 1080" convention shown in the
// Figma "Images" sidebar mock.
export function formatAspectRatio(width, height) {
  if (!width || !height) return null
  const ratio = width / height

  let closestLabel = null
  let closestDiff = Infinity
  KNOWN_RATIOS.forEach(({ label, value }) => {
    const diff = Math.abs(value - ratio)
    if (diff < closestDiff) {
      closestDiff = diff
      closestLabel = label
    }
  })
  if (closestDiff < KNOWN_RATIO_TOLERANCE) return closestLabel

  return ratio >= 1 ? `${ratio.toFixed(2)}:1` : `1:${(1 / ratio).toFixed(2)}`
}

export function formatMediaDimensions(media) {
  if (!media?.width || !media?.height) return null
  const ratio = formatAspectRatio(media.width, media.height)
  return `${ratio} • ${media.width} × ${media.height}`
}

// All media items whose pixel dimensions classify as `label` (same labels
// the Images sidebar shows via formatAspectRatio), preserving array order.
// Used by Google Display/Demand Gen placement builders so every matching
// asset gets its own preview card — same "one card per asset" rule as
// findAllVideoMedia.
export function findAllMediaByRatioLabel(mediaList, label) {
  return (mediaList ?? []).filter(
    (media) =>
      media?.width && media?.height && formatAspectRatio(media.width, media.height) === label,
  )
}

// First match only — for callers that intentionally pick a single
// representative asset (e.g. Display "General" fallback).
export function findMediaByRatioLabel(mediaList, label) {
  return findAllMediaByRatioLabel(mediaList, label)[0]
}

// A media item is "video" if it's a real uploaded video (`type` starting with
// `video/`) or a YouTube passthrough (`youtube_video_id` set, no `url`/dimensions).
export const isVideoMedia = (media) =>
  Boolean(media?.type?.includes('video')) || Boolean(media?.youtube_video_id)

// Returns the first video/YouTube item from a media list for video-only slots.
// Warns in dev when multiple are present, since the preview card only shows one.
export function findVideoMedia(mediaList) {
  const videos = (mediaList ?? []).filter(isVideoMedia)
  if (videos.length > 1 && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      `Ad preview: creative has ${videos.length} video media items; showing only the first (id: ${videos[0]?.id}). No design exists yet for a multi-video preview card.`,
    )
  }
  return videos[0]
}

// All video/YouTube items in array order — for groups that render one card per video.
export function findAllVideoMedia(mediaList) {
  return (mediaList ?? []).filter(isVideoMedia)
}

export function getYoutubeThumbnailUrl(youtubeVideoId) {
  return `https://i.ytimg.com/vi/${youtubeVideoId}/hqdefault.jpg`
}
